import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import type { InventoryItem, InventoryStockAdjustReason } from '@court-sync/shared';
import {
  Button,
  Field,
  FieldLabel,
  Input,
  Modal,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/ui';
import { clubInventoryService } from '@/services/clubInventory';

const ADJUST_FORM_ID = 'inventory-stock-adjust-form';

const REASON_OPTIONS: { value: InventoryStockAdjustReason; label: string }[] = [
  { value: 'received', label: 'Received' },
  { value: 'used', label: 'Used' },
  { value: 'lost', label: 'Lost' },
  { value: 'sold', label: 'Sold' },
  { value: 'other', label: 'Other' },
];

interface InventoryStockAdjustFormValues {
  delta: string;
  reason: InventoryStockAdjustReason;
  note: string;
}

interface InventoryStockAdjustModalProps {
  clubId: string;
  item: InventoryItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function InventoryStockAdjustModal({
  clubId,
  item,
  isOpen,
  onClose,
  onSaved,
}: InventoryStockAdjustModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm<InventoryStockAdjustFormValues>({
    defaultValues: { delta: '1', reason: 'received', note: '' },
  });

  const reason = watch('reason');

  useEffect(() => {
    if (isOpen) {
      reset({ delta: '1', reason: 'received', note: '' });
    }
  }, [isOpen, reset]);

  const onSubmit = async (values: InventoryStockAdjustFormValues) => {
    if (!item) return;
    const delta = Number.parseInt(values.delta, 10);
    if (!Number.isFinite(delta) || delta === 0) {
      toast.error('Enter a non-zero adjustment');
      return;
    }

    const res = await clubInventoryService.adjustStock(clubId, item.id, {
      delta,
      reason: values.reason,
      note: values.note.trim() || null,
    });

    if (res.success) {
      toast.success('Stock updated');
      onSaved();
      onClose();
    } else {
      toast.error(res.error || 'Failed to adjust stock');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Adjust stock" stackLevel="elevated" maxWidth="md">
      {item ? (
        <p className="mb-4 text-sm text-muted-foreground">Current quantity: {item.quantity}</p>
      ) : null}
      <form id={ADJUST_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Field>
          <FieldLabel htmlFor="stock-delta">Change (+/-)</FieldLabel>
          <Input
            id="stock-delta"
            type="number"
            placeholder="e.g. 10 or -2"
            {...register('delta')}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="stock-reason">Reason</FieldLabel>
          <Select value={reason} onValueChange={(v) => setValue('reason', v as InventoryStockAdjustReason)}>
            <SelectTrigger id="stock-reason">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REASON_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field>
          <FieldLabel htmlFor="stock-note">Note</FieldLabel>
          <Textarea id="stock-note" rows={2} placeholder="Optional" {...register('note')} />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            Save adjustment
          </Button>
        </div>
      </form>
    </Modal>
  );
}
